import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, onValue, update, push } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import { kvaOptions, makeOptions } from '../../constants';
import { formatDate } from '../../Utils/dateUtils';
import {
  FaArrowLeft,
  FaPrint,
  FaCheck,
  FaTimes,
  FaSearch,
  FaFileAlt,
  FaEdit,
  FaTrash,
  FaSave,
  FaBan,
  FaFileExport,
  FaFileInvoice,
  FaOilCan,
  FaEllipsisV,
  FaBolt
} from 'react-icons/fa';

const sanitizeKey = (key) => {
  return key.replace(/[.#$/[\]]/g, '_');
};

const RegisterView = () => {
  const { division } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // State declarations
  const [company, setCompany] = useState('');
  const [originalData, setOriginalData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [actionMode, setActionMode] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [moNumbers, setMoNumbers] = useState({});
  const [showMoInputs, setShowMoInputs] = useState(false);
  const [workOrderNo, setWorkOrderNo] = useState('');
  const [workOrderDate, setWorkOrderDate] = useState('');
  const [oilStats, setOilStats] = useState({
    received: '0.00',
    used: '0.00',
    balance: '0.00',
    completedJobs: 0,
    pendingJobs: 0
  });
  const [finalVehicleNo, setFinalVehicleNo] = useState('');
  const [editedChallanTotals, setEditedChallanTotals] = useState({});

  // Load company
  useEffect(() => {
    const storedCompany = localStorage.getItem('selectedCompany') || localStorage.getItem('userCompany');
    if (!storedCompany) {
      navigate('/login');
    } else {
      const sanitizedCompany = sanitizeKey(storedCompany);
      setCompany(sanitizedCompany);
      localStorage.setItem('selectedCompany', sanitizedCompany);
    }
  }, [navigate]);

  // Load and process data from Firebase
  useEffect(() => {
    if (!company || !division) return;

    setIsLoading(true);
    const sanitizedCompany = sanitizeKey(company);
    const sanitizedDivision = sanitizeKey(division);
    
    const pathsToTry = [
      `companies/${sanitizedCompany}/${sanitizedDivision}/register`,
      `companies/${sanitizedCompany}/${sanitizedDivision}/entryPass`,
      `${sanitizedCompany}/${sanitizedDivision}/register`,
      `${sanitizedCompany}/${sanitizedDivision}/entryPass`
    ];

    const unsubs = [];
    let dataLoaded = false;

    pathsToTry.forEach(path => {
      const dbRef = ref(realtimeDb, path);
      const unsub = onValue(dbRef, (snapshot) => {
        if (dataLoaded && path !== pathsToTry[0]) return; // Prevent duplicate updates from multiple paths
        
        const val = snapshot.val() || {};
        const entries = Object.entries(val);
        const seenChallans = new Set();

        const sortedEntries = entries.sort((a, b) => {
          const challanA = parseInt(a[1].challanNo) || 0;
          const challanB = parseInt(b[1].challanNo) || 0;
          if (challanA !== challanB) return challanA - challanB;
          return (a[1].timestamp || 0) - (b[1].timestamp || 0);
        });

        let lastChallanNo = 0;
        let currentSrNo = 1;
        const rows = [];
        const challanMap = {};

        sortedEntries.forEach(([id, entry]) => {
          const challanNo = parseInt(entry.challanNo) || 0;

          if (challanNo > lastChallanNo) {
            for (let i = lastChallanNo + 1; i < challanNo; i++) {
              challanMap[i] = { nextSrNo: currentSrNo };
            }
            lastChallanNo = challanNo;
          }

          if (challanMap[challanNo]?.nextSrNo) {
            currentSrNo = challanMap[challanNo].nextSrNo;
            delete challanMap[challanNo];
          }

          rows.push({
            id,
            sourcePath: path, // Store the source path
            ...entry,
            makeSrNo: entry.makeSrNo || '-',
            oilCap: entry.oilCap || '0.00',
            usedOil: entry.usedOil !== undefined ? entry.usedOil : null,
            totalOil: entry.totalOil || '0.00',
            oilChallanNo: entry.oilChallanNo || 'N/A',
            srNo: currentSrNo++,
            permanentSrNo: currentSrNo - 1
          });

          if (!seenChallans.has(challanNo)) {
            seenChallans.add(challanNo);
          }
        });

        const groupedRows = {};
        rows.forEach(row => {
          const challanKey = row.challanNo || 'No Challan';
          if (!groupedRows[challanKey]) {
            groupedRows[challanKey] = [];
          }
          groupedRows[challanKey].push(row);
        });

        let totalReceived = 0;
        let totalUsed = 0;

        Object.values(groupedRows).forEach(group => {
           const firstRow = group[0] || {};
           
           // Received
           const received = firstRow.challanTotalOil !== undefined && firstRow.challanTotalOil !== '' 
             ? Number(firstRow.challanTotalOil) 
             : (Number(firstRow.totalOil) || 0);
           totalReceived += received;
           
           // Used
           const used = firstRow.challanUsed !== undefined && firstRow.challanUsed !== '' 
             ? Number(firstRow.challanUsed)
             : group.reduce((sum, row) => {
                 const usedOilValue = row.usedOil !== null ? row.usedOil : row.oilCap;
                 return sum + (row.completed ? Number(usedOilValue) || 0 : 0);
               }, 0);
           totalUsed += used;
        });

        const totalBalance = totalReceived - totalUsed;
        const completedJobs = rows.filter(row => row.completed).length;
        const pendingJobs = rows.length - completedJobs;

        setOilStats({
          received: totalReceived.toFixed(2),
          used: totalUsed.toFixed(2),
          balance: totalBalance.toFixed(2),
          completedJobs,
          pendingJobs
        });

        setOriginalData(rows);
        dataLoaded = true;
        setIsLoading(false);
      }, (error) => {
        console.error('Error fetching data:', error);
        setIsLoading(false);
        alert(`Failed to fetch data: ${error.message}`);
      });

      unsubs.push(unsub);
    });

    return () => unsubs.forEach(unsub => unsub());
  }, [company, division]);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...originalData];

    // Apply status filter
    if (statusFilter === 'pending') {
      filtered = filtered.filter(item => !item.completed);
    } else if (statusFilter === 'completed') {
      filtered = filtered.filter(item => item.completed);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item => {
        // Normalize fields for case-insensitive search
        const jobNo = item.jobNo ? item.jobNo.toLowerCase() : '';
        const challanNo = item.challanNo ? item.challanNo.toLowerCase() : '';
        const finalChallanNo = item.finalChallanNo ? item.finalChallanNo.toLowerCase() : '';
        const date = item.date ? item.date.toLowerCase() : '';
        const dispatchDate = item.dispatchDate ? item.dispatchDate.toLowerCase() : '';
        const makeSrNo = item.makeSrNo ? item.makeSrNo.toLowerCase() : '';
        const oilChallanNo = item.oilChallanNo && item.oilChallanNo !== 'N/A' ? item.oilChallanNo.toLowerCase() : '';

        // Check if any field matches the search query
        return (
          jobNo.includes(query) ||
          challanNo.includes(query) ||
          finalChallanNo.includes(query) ||
          date.includes(query) ||
          dispatchDate.includes(query) ||
          makeSrNo.includes(query) ||
          oilChallanNo.includes(query)
        );
      });
    }

    setFilteredData(filtered);

    // Group filtered data by challan number
    const groupedByChallan = {};
    filtered.forEach(item => {
      const challanKey = item.challanNo || 'No Challan';
      if (!groupedByChallan[challanKey]) {
        groupedByChallan[challanKey] = [];
      }
      groupedByChallan[challanKey].push(item);
    });

    setGrouped(groupedByChallan);
  }, [originalData, searchQuery, statusFilter]);

  const kvaStats = useMemo(() => {
    const stats = {};
    originalData.forEach(job => {
      const kva = job.kva || 'Unknown';
      if (!stats[kva]) {
        stats[kva] = { completed: 0, pending: 0 };
      }
      if (job.completed) {
        stats[kva].completed += 1;
      } else {
        stats[kva].pending += 1;
      }
    });
    return stats;
  }, [originalData]);

  const getChallanTotals = (rows) => {
    const firstRow = rows[0] || {};
    
    const received = firstRow.challanTotalOil !== undefined && firstRow.challanTotalOil !== '' 
      ? Number(firstRow.challanTotalOil) 
      : (Number(firstRow.totalOil) || 0);
      
    const oilChallanNo = firstRow.challanOilChallanNo !== undefined && firstRow.challanOilChallanNo !== '' 
      ? firstRow.challanOilChallanNo 
      : (firstRow.oilChallanNo || 'N/A');

    const used = firstRow.challanUsed !== undefined && firstRow.challanUsed !== '' 
      ? Number(firstRow.challanUsed)
      : rows.reduce((sum, row) => {
          const usedOilValue = row.usedOil !== null ? row.usedOil : row.oilCap;
          return sum + (row.completed ? Number(usedOilValue) || 0 : 0);
        }, 0);
        
    const totalOilWithPercentage = firstRow.challanTotalOilPercentage !== undefined && firstRow.challanTotalOilPercentage !== ''
      ? Number(firstRow.challanTotalOilPercentage)
      : rows.reduce((sum, row) => sum + (Number(row.oilCap) || 0), 0) * 1.03;
      
    const balance = firstRow.challanBalance !== undefined && firstRow.challanBalance !== ''
      ? Number(firstRow.challanBalance)
      : received - used;

    const savedTotalKg = firstRow.challanTotalKg;
    let totalKg;
    if (savedTotalKg !== undefined && savedTotalKg !== null && savedTotalKg !== '') {
      totalKg = Number(savedTotalKg);
    } else {
      totalKg = rows.reduce((sum, row) => sum + (Number(row.kg) || 0), 0);
    }

    return {
      received: received.toFixed(2),
      oilChallanNo,
      used: used.toFixed(2),
      balance: balance.toFixed(2),
      completed: rows.filter(row => row.completed).length,
      pending: rows.filter(row => !row.completed).length,
      totalOilWithPercentage: totalOilWithPercentage.toFixed(2),
      totalKg: totalKg.toFixed(2)
    };
  };

  const toggleSelect = (id) => {
    if (!actionMode) return;
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one job to delete.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete the selected jobs?')) return;

    const sanitizedCompany = sanitizeKey(company);
    const sanitizedDivision = sanitizeKey(division);
    const updates = {};
    selectedRows.forEach(id => {
      updates[`companies/${sanitizedCompany}/${sanitizedDivision}/entryPass/${id}`] = null;
      updates[`companies/${sanitizedCompany}/${sanitizedDivision}/register/${id}`] = null;
    });

    try {
      await update(ref(realtimeDb), updates);
      setSelectedRows([]);
      setActionMode(null);
      alert('Selected jobs deleted successfully.');
    } catch (error) {
      console.error('Error deleting jobs:', error);
      alert(`Failed to delete jobs: ${error.message}`);
    }
  };

  const startEdit = () => {
    const mapped = {};
    originalData.forEach(row => {
      mapped[row.id] = { 
        ...row,
        jobNo: row.jobNo || '',
        kva: row.kva || '',
        make: row.make || '',
        makeSrNo: row.makeSrNo || '',
        oilCap: row.oilCap || '0.00',
        kg: row.kg || '',
        finalChallanNo: row.finalChallanNo || '',
        dispatchDate: row.dispatchDate || '',
        completed: row.completed || false
      };
    });
    setEditedData(mapped);
    setEditMode(true);
    
    const initialTotals = {};
    Object.entries(grouped).forEach(([challanNo, rows]) => {
      const firstRow = rows[0] || {};
      initialTotals[challanNo] = {
        totalKg: firstRow.challanTotalKg,
        received: firstRow.challanTotalOil,
        totalOilWithPercentage: firstRow.challanTotalOilPercentage,
        oilChallanNo: firstRow.challanOilChallanNo,
        used: firstRow.challanUsed,
        balance: firstRow.challanBalance
      };
    });
    setEditedChallanTotals(initialTotals);
  };

  const saveEdit = async () => {
    if (Object.keys(editedData).length === 0) {
      alert('No changes to save.');
      return;
    }

    const sanitizedCompany = sanitizeKey(company);
    const sanitizedDivision = sanitizeKey(division);
    const updates = {};

    // Prepare updates and optimistic state changes
    const updatedData = [...originalData];

    // Inject challan-level updates into editedData for all rows in a challan if it changed
    originalData.forEach(row => {
       const challanNo = row.challanNo || 'No Challan';
       const editedChallanTotal = editedChallanTotals[challanNo] || {};
       
       const fieldsMap = {
         challanTotalKg: editedChallanTotal.totalKg,
         challanTotalOil: editedChallanTotal.received,
         challanTotalOilPercentage: editedChallanTotal.totalOilWithPercentage,
         challanOilChallanNo: editedChallanTotal.oilChallanNo,
         challanUsed: editedChallanTotal.used,
         challanBalance: editedChallanTotal.balance
       };
       
       Object.entries(fieldsMap).forEach(([field, value]) => {
          if (value !== undefined && value !== String(row[field] || '') && (value !== '' || row[field] !== undefined)) {
             if (!editedData[row.id]) {
                 editedData[row.id] = { ...row };
             }
             editedData[row.id][field] = value;
          }
       });
    });

    Object.keys(editedData).forEach(id => {
      const originalRow = originalData.find(d => d.id === id);
      if (!originalRow) return;
      const editedRow = editedData[id];
      
      // Only include fields that have changed
      const changes = {};
      Object.keys(editedRow).forEach(key => {
        if (key === 'id' || key === 'srNo' || key === 'permanentSrNo') return; // Skip non-editable fields
        if (JSON.stringify(originalRow[key]) !== JSON.stringify(editedRow[key])) {
          changes[key] = editedRow[key];
        }
      });

      if (Object.keys(changes).length > 0) {
        // Create the update object with all fields to preserve data
        const updateData = {
          ...originalRow,
          ...changes,
          lastUpdated: Date.now()
        };

        // Update Firebase paths
        const originalPath = originalRow.sourcePath || `companies/${sanitizedCompany}/${sanitizedDivision}/register`;
        updates[`${originalPath}/${id}`] = updateData;
        if (originalPath.includes('register')) {
          updates[`companies/${sanitizedCompany}/${sanitizedDivision}/entryPass/${id}`] = updateData;
        } else if (originalPath.includes('entryPass')) {
          updates[`companies/${sanitizedCompany}/${sanitizedDivision}/register/${id}`] = updateData;
        }

        // Optimistically update local state
        const index = updatedData.findIndex(row => row.id === id);
        if (index !== -1) {
          updatedData[index] = { ...updatedData[index], ...changes, lastUpdated: Date.now() };
        }
      }
    });

    if (Object.keys(updates).length === 0) {
      alert('No actual changes detected.');
      return;
    }

    try {
      await update(ref(realtimeDb), updates);
      // Apply optimistic updates to state
      setOriginalData(updatedData);
      setEditMode(false);
      setSelectedRows([]);
      setEditedData({});
      setEditedChallanTotals({});
      setActionMode(null);
      alert('Changes saved successfully.');
    } catch (error) {
      console.error('Error saving changes:', error);
      alert(`Failed to save changes: ${error.message}`);
    }
  };

  const handleGenerateBill = () => {
    if (!showMoInputs) {
      const validJobs = originalData.filter(
        (job) => selectedRows.includes(job.id) && job.completed && !job.workBillNo
      );

      if (validJobs.length === 0) {
        alert("Please select at least one completed job with no existing bill.");
        return;
      }

      setShowMoInputs(true);
    } else {
      if (!workOrderNo || !workOrderDate) {
        alert("Please enter Work Order Number and Date.");
        return;
      }

      const missingMoNumbers = selectedRows.some(
        (rowId) => !moNumbers[rowId]?.trim()
      );

      if (missingMoNumbers) {
        alert("Please enter M.O. Numbers for all selected jobs.");
        return;
      }

      navigate('/print/bill', {
        state: {
          company,
          division,
          ids: selectedRows,
          workOrderNo,
          workOrderDate,
          moNumbers,
        },
      });
    }
  };

  const handleGenerateGatePass = async () => {
    const pendingJobs = originalData.filter(job =>
      selectedRows.includes(job.id) && !job.completed
    );

    if (pendingJobs.length === 0) {
      alert("Please select at least one pending job.");
      return;
    }

    const sanitizedCompany = sanitizeKey(company);
    const sanitizedDivision = sanitizeKey(division);
    const gatePassNumber = `GP_${Date.now()}`;
    const gatePassDate = new Date().toISOString().split('T')[0];
    const baseTotalOil = pendingJobs.reduce((sum, job) => sum + (Number(job.oilCap) || 0), 0);
    const totalOilUsed = (baseTotalOil * 1.03).toFixed(2);

    const jobDetails = {};
    pendingJobs.forEach(job => {
      jobDetails[job.id] = {
        jobNo: job.jobNo,
        kva: job.kva,
        make: job.make,
        makeSrNo: job.makeSrNo,
        type: job.type || 'N/A',
        oilCap: job.oilCap,
        kg: job.kg || ''
      };
    });

    const gatePassData = {
      gatePassNumber,
      gatePassDate,
      finalVehicleNo,
      totalOilUsed,
      jobDetails,
      timestamp: Date.now(),
      status: 'Generated'
    };

    try {
      const gatePassRef = ref(realtimeDb, `companies/${sanitizedCompany}/${sanitizedDivision}/gatePassSummary`);
      await push(gatePassRef, gatePassData);

      navigate(`/print/gatepass/${sanitizedCompany}/${sanitizedDivision}/${selectedRows.join(',')}`, {
        state: {
          entries: pendingJobs.sort((a, b) => a.srNo - b.srNo),
          company,
          division,
          gatePassNumber,
          gatePassDate,
          finalVehicleNo,
          totalOilUsed
        }
      });
    } catch (error) {
      console.error('Error storing gate pass:', error);
      alert(`Failed to generate gate pass: ${error.message}`);
    }
  };

  const cancelAction = () => {
    setActionMode(null);
    setSelectedRows([]);
    setEditedData({});
    setEditMode(false);
    setEditedChallanTotals({});
    setMoNumbers({});
    setShowMoInputs(false);
    setWorkOrderNo('');
    setWorkOrderDate('');
    setFinalVehicleNo('');
    setIsMobileMenuOpen(false);
  };

  const handleChange = (id, field, value) => {
    setEditedData(prev => ({
      ...prev,
      [id]: { 
        ...prev[id], 
        [field]: value,
        ...(field === 'completed' && value === true ? { completedDate: new Date().toISOString().split('T')[0] } : {})
      }
    }));
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    let printContent = '';
    const currentDate = new Date().toLocaleDateString();

    const dataToPrint = statusFilter === 'all'
      ? originalData
      : statusFilter === 'pending'
        ? originalData.filter(item => !item.completed)
        : originalData.filter(item => item.completed);

    const groupedToPrint = {};
    dataToPrint.forEach(item => {
      const challanKey = item.challanNo || 'No Challan';
      if (!groupedToPrint[challanKey]) {
        groupedToPrint[challanKey] = [];
      }
      groupedToPrint[challanKey].push(item);
    });

    printContent = `
      <div class="print-header">
        <h1>${company}</h1>
        <h2>${division} Register - ${statusFilter === 'all' ? 'All Jobs' : statusFilter === 'pending' ? 'Pending Jobs' : 'Completed Jobs'}</h2>
        <div class="summary">
          Printed: ${currentDate} | Total: ${dataToPrint.length} jobs | 
          Completed: ${dataToPrint.filter(j => j.completed).length} | 
          Pending: ${dataToPrint.filter(j => !j.completed).length}
        </div>
      </div>
    `;

    const challans = Object.entries(groupedToPrint).sort((a, b) => {
      const dateA = new Date(a[1][0]?.date || 0).getTime();
      const dateB = new Date(b[1][0]?.date || 0).getTime();
      if (dateB !== dateA) return dateB - dateA;
      return parseInt(b[0] || 0) - parseInt(a[0] || 0);
    });
    let currentPageHeight = 0;
    let pageContent = '';

    challans.forEach(([challanNo, rows], index) => {
      const approxChallanHeight = 10 + (rows.length * 5);

      if (currentPageHeight + approxChallanHeight > 270) {
        printContent += `<div class="page">${pageContent}</div>`;
        pageContent = '';
        currentPageHeight = 0;
      }

      const totals = getChallanTotals(rows);
      const challanHeader = `
        <div class="challan-header">
          <strong>Challan ${challanNo}</strong> | 
          Date: ${formatDate(rows[0]?.date) || '-'} | 
          Vehicle: ${rows[0]?.vehicleNo || '-'} | 
          Oil: ${totals.received}L (R) | 
          Total Oil in %: ${totals.totalOilWithPercentage}L | 
          Oil Challan: ${totals.oilChallanNo} | 
          Used: ${totals.used}L (U)
        </div>
      `;

      const tableRows = rows.map(row => `
        <tr class="${row.completed ? 'completed-row' : 'pending-row'}">
          <td>${row.srNo}</td>
          <td>${row.jobNo}</td>
          <td>${row.kva}</td>
          <td>${row.make}</td>
          <td>${row.makeSrNo || '-'}</td>
          <td>${row.oilCap}L</td>
          <td>${row.completed ? (row.usedOil !== null ? row.usedOil : row.oilCap) : '-'}L</td>
          <td>${row.finalChallanNo || '-'}</td>
          <td>${formatDate(row.dispatchDate) || '-'}</td>
          <td>${row.workOrderNo || '-'}</td>
          <td>${row.moNumber || '-'}</td>
          <td>${row.workBillNo || '-'}</td>
          <td>${formatDate(row.billDate) || '-'}</td>
          <td class="status ${row.completed ? 'completed' : 'pending'}">
            ${row.completed ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">Completed</span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800">Pending</span>}
          </td>
        </tr>
      `).join('');

      pageContent += `
        <div class="challan-block">
          ${challanHeader}
          <table>
            <thead>
              <tr>
                <th>SR</th>
                <th>Job No</th>
                <th>KVA</th>
                <th>Make</th>
                <th>Make SrNo</th>
                <th>Oil Cap</th>
                <th>Used Oil</th>
                <th>Final Chl.</th>
                <th>Dispatch</th>
                <th>Work Ord.</th>
                <th>M.O. No</th>
                <th>Bill No</th>
                <th>Bill Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </div>
      `;

      currentPageHeight += approxChallanHeight;

      if (index === challans.length - 1) {
        printContent += `<div class="page">${pageContent}</div>`;
      }
    });

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${company} - ${division} Register</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 8pt;
              line-height: 1.1;
            }
            .print-header {
              text-align: center;
              margin-bottom: 5mm;
              padding-bottom: 2mm;
              border-bottom: 1px solid #ddd;
            }
            .print-header h1 {
              margin: 0 0 2px 0;
              font-size: 12pt;
            }
            .print-header h2 {
              margin: 0 0 3px 0;
              font-size: 10pt;
            }
            .summary {
              font-size: 8pt;
              color: #555;
            }
            .page {
              page-break-after: always;
              height: 277mm;
              overflow: hidden;
            }
            .page:last-child {
              page-break-after: auto;
            }
            .challan-block {
              margin-bottom: 5mm;
            }
            .challan-header {
              font-size: 8pt;
              margin-bottom: 2mm;
              padding: 1mm 0;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 3mm;
              font-size: 7pt;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 1mm 1mm;
              text-align: left;
            }
            th {
              background-color: #f5f5f5;
              font-weight: bold;
              white-space: nowrap;
            }
            .status {
              text-align: center;
              font-weight: bold;
            }
            .completed {
              color: #28a745;
            }
            .pending {
              color: #dc3545;
            }
            .completed-row {
              background-color: #e6ffe6;
            }
            .pending-row {
              background-color: #fff3e0;
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            setTimeout(() => {
              window.print();
              window.close();
            }, 200);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="sm:hidden sticky top-0 z-50 bg-gradient-to-r from-blue-600 to-blue-800 text-white px-4 py-3 shadow-lg flex justify-between items-center">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="p-1 rounded-full hover:bg-blue-700 transition-colors duration-200"
          >
            <FaArrowLeft className="text-lg" />
          </button>
          <div>
            <h1 className="text-lg font-bold">{division}</h1>
            <p className="text-xs">{company}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="p-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white"
          >
            <FaPrint className="text-lg" />
          </button>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-blue-600 transition-colors duration-200"
          >
            <FaEllipsisV className="text-lg" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="sm:hidden fixed top-16 right-2 z-40 bg-white rounded-lg shadow-xl border border-gray-200 w-48 py-2">
          <button
            onClick={() => { setActionMode('gatepass'); setShowMoInputs(false); setIsMobileMenuOpen(false); }}
            className={`w-full text-left px-4 py-2 flex items-center gap-2 ${actionMode === 'gatepass' ? 'bg-purple-100 text-purple-800' : 'hover:bg-gray-100'}`}
          >
            <FaFileExport size={14} /> Gate Pass
          </button>
          <button
            onClick={() => { setActionMode('bill'); setShowMoInputs(false); setIsMobileMenuOpen(false); }}
            className={`w-full text-left px-4 py-2 flex items-center gap-2 ${actionMode === 'bill' ? 'bg-green-100 text-green-800' : 'hover:bg-gray-100'}`}
          >
            <FaFileInvoice size={14} /> Bill
          </button>
          <button
            onClick={() => { setActionMode('edit'); startEdit(); setIsMobileMenuOpen(false); }}
            className={`w-full text-left px-4 py-2 flex items-center gap-2 ${actionMode === 'edit' ? 'bg-amber-100 text-amber-800' : 'hover:bg-gray-100'}`}
          >
            <FaEdit size={14} /> Edit
          </button>
          <button
            onClick={() => { setActionMode('delete'); setIsMobileMenuOpen(false); }}
            className={`w-full text-left px-4 py-2 flex items-center gap-2 ${actionMode === 'delete' ? 'bg-red-100 text-red-800' : 'hover:bg-gray-100'}`}
          >
            <FaTrash size={14} /> Delete
          </button>
        </div>
      )}

      {/* Desktop Header */}
      <div className="hidden sm:block sticky top-0 z-50 bg-white border-b border-gray-200 px-6 py-4 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 no-print">
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-blue-600 transition-colors duration-200"
          >
            <FaArrowLeft className="text-lg" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight whitespace-nowrap">{company} <span className="text-blue-600">- {division} Register</span></h1>
            <div className="text-sm font-medium flex flex-wrap gap-4 mt-3">
              <div className="bg-white rounded-xl shadow-sm p-3 border border-gray-200 w-full sm:w-auto min-w-[300px]">
                <div className="flex items-center text-gray-800 mb-2">
                  <FaOilCan className="mr-2 text-blue-600" />
                  <h3 className="text-sm font-bold tracking-wide">Oil Inventory</h3>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 px-3 py-2 rounded-lg border-l-4 border-blue-500">
                    <p className="text-blue-600 text-xs font-bold uppercase tracking-wider">Received</p>
                    <p className="text-gray-900 font-extrabold text-sm">{oilStats.received} L</p>
                  </div>
                  <div className="bg-emerald-50 px-3 py-2 rounded-lg border-l-4 border-emerald-500">
                    <p className="text-emerald-600 text-xs font-bold uppercase tracking-wider">Used</p>
                    <p className="text-gray-900 font-extrabold text-sm">{oilStats.used} L</p>
                  </div>
                  <div className={`px-3 py-2 rounded-lg border-l-4 shadow-md ${Number(oilStats.balance) >= 0
                    ? 'bg-emerald-50 border-emerald-500'
                    : 'bg-rose-50 border-rose-500'
                    }`}>
                    <p className={`text-xs font-bold uppercase tracking-wider ${Number(oilStats.balance) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      Balance
                    </p>
                    <p className={`font-extrabold text-sm text-gray-900`}>
                      {oilStats.balance} L
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-3 border border-gray-200 w-full sm:w-auto min-w-[300px]">
                <div className="flex items-center text-gray-800 mb-2 border-b border-gray-100 pb-2">
                  <FaBolt className="mr-2 text-yellow-500" />
                  <h3 className="text-sm font-bold tracking-wide">KVA Summary</h3>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-200">
                  {Object.entries(kvaStats).sort((a, b) => Number(a[0]) - Number(b[0])).map(([kva, stat]) => (
                    <div key={kva} className="bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200 flex flex-col min-w-[70px] flex-shrink-0">
                      <p className="text-[11px] font-bold text-gray-600 text-center mb-1">{kva} KVA</p>
                      <div className="flex justify-between text-[10px] gap-2">
                        <div className="flex flex-col items-center">
                          <span className="text-emerald-600 font-bold">{stat.completed}</span>
                          <span className="text-gray-400 text-[8px] uppercase">C</span>
                        </div>
                        <div className="flex flex-col items-center">
                          <span className="text-rose-600 font-bold">{stat.pending}</span>
                          <span className="text-gray-400 text-[8px] uppercase">P</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-end">
            <button
            onClick={() => { setActionMode('gatepass'); setShowMoInputs(false); }}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200 shadow ${actionMode === 'gatepass' ? 'bg-purple-600 text-white' : 'bg-purple-500 hover:bg-purple-600 text-white'}`}
          >
            <FaFileExport size={16} /> Gate Pass
          </button>
          <button
            onClick={() => { setActionMode('bill'); setShowMoInputs(false); }}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200 shadow ${actionMode === 'bill' ? 'bg-green-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
          >
            <FaFileInvoice size={16} /> Bill
          </button>
          <button
            onClick={() => { setActionMode('edit'); startEdit(); }}
            className={`px-3 py-1 rounded-lg flex items-center gap-1 transition-colors duration-200 shadow ${actionMode === 'edit' ? 'bg-amber-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
          >
            <FaEdit size={14} /> Edit
          </button>
          <button
            onClick={() => { setActionMode('delete'); }}
            className={`px-3 py-1 rounded-lg flex items-center gap-1 transition-colors duration-200 shadow ${actionMode === 'delete' ? 'bg-red-600 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}`}
          >
            <FaTrash size={14} /> Delete
          </button>
          {actionMode === 'edit' && editMode && (
            <>
              <button
                onClick={saveEdit}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200 shadow"
              >
                <FaSave size={16} /> Save
              </button>
              <button
                onClick={cancelAction}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200 shadow"
              >
                <FaBan size={16} /> Cancel
              </button>
            </>
          )}
          {actionMode === 'delete' && selectedRows.length > 0 && (
            <button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200 shadow"
            >
              <FaCheck size={16} /> Confirm Delete
            </button>
          )}
          <button
            onClick={handlePrint}
            className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg flex items-center gap-2 transition-colors duration-200 shadow"
          >
            <FaPrint className="text-lg" />
            <span className="hidden sm:inline">Print</span>
          </button>
        </div>
      </div>

      {/* Filter and search bar */}
      <div className="sticky sm:top-[76px] top-[60px] z-1000 bg-white px-4 sm:px-6 py-3 shadow-md flex flex-wrap items-center justify-between gap-2 border-b no-print">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 text-sm rounded-lg font-medium transition-all duration-200 flex items-center gap-1 ${statusFilter === 'all'
                ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-200'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 shadow-sm'
              }`}
          >
            <span className="w-2 h-2 rounded-full bg-blue-400"></span>
            All ({originalData.length})
          </button>
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-4 py-2 text-sm rounded-lg font-medium transition-all duration-200 flex items-center gap-1 ${statusFilter === 'pending'
                ? 'bg-rose-600 text-white shadow-md ring-2 ring-rose-200'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 shadow-sm'
              }`}
          >
            <span className="w-2 h-2 rounded-full bg-red-400"></span>
            Pending ({oilStats.pendingJobs})
          </button>
          <button
            onClick={() => setStatusFilter('completed')}
            className={`px-4 py-2 text-sm rounded-lg font-medium transition-all duration-200 flex items-center gap-1 ${statusFilter === 'completed'
                ? 'bg-emerald-600 text-white shadow-md ring-2 ring-emerald-200'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 shadow-sm'
              }`}
          >
            <span className="w-2 h-2 rounded-full bg-green-400"></span>
            Completed ({oilStats.completedJobs})
          </button>
        </div>
        <div className="relative w-full sm:w-auto sm:flex-1 sm:max-w-md">
          <FaSearch className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search Challan No / Final Challan No / Job No / Date"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
          />
        </div>
      </div>

      {/* Oil Inventory for Mobile */}
      <div className="sm:hidden bg-white px-4 py-3 shadow-md border-b no-print">
        <div className="flex items-center text-gray-700 mb-2">
          <FaOilCan className="mr-2 text-blue-600" />
          <h3 className="text-sm font-bold tracking-wide">Oil Inventory</h3>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-gray-100 px-3 py-1.5 rounded-lg border-l-2 border-blue-400 shadow-sm">
            <p className="text-blue-300 text-xs">Received</p>
            <p className="text-gray-800 font-bold text-sm">{oilStats.received} L</p>
          </div>
          <div className="bg-gray-100 px-3 py-1.5 rounded-lg border-l-2 border-green-400 shadow-sm">
            <p className="text-green-300 text-xs">Used</p>
            <p className="text-gray-800 font-bold text-sm">{oilStats.used} L</p>
          </div>
          <div className="bg-gray-100 px-3 py-1.5 rounded-lg border-l-2 border-yellow-400 shadow-sm">
            <p className={`${Number(oilStats.balance) >= 0 ? 'text-green-300' : 'text-red-300'} text-xs`}>Balance</p>
            <p className={`${Number(oilStats.balance) >= 0 ? 'text-gray-800' : 'text-red-500'} font-bold text-sm`}>{oilStats.balance} L</p>
          </div>
        </div>
      </div>

      {/* KVA Summary for Mobile */}
      <div className="sm:hidden bg-white px-4 py-3 shadow-md border-b no-print">
        <div className="flex items-center text-gray-700 mb-2 border-b border-gray-100 pb-2">
          <FaBolt className="mr-2 text-yellow-500" />
          <h3 className="text-sm font-bold tracking-wide">KVA Summary</h3>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-200">
          {Object.entries(kvaStats).sort((a, b) => Number(a[0]) - Number(b[0])).map(([kva, stat]) => (
            <div key={kva} className="bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200 flex flex-col min-w-[70px] flex-shrink-0">
              <p className="text-[11px] font-bold text-gray-600 text-center mb-1">{kva} KVA</p>
              <div className="flex justify-between text-[10px] gap-2">
                <div className="flex flex-col items-center">
                  <span className="text-emerald-600 font-bold">{stat.completed}</span>
                  <span className="text-gray-400 text-[8px] uppercase">C</span>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-rose-600 font-bold">{stat.pending}</span>
                  <span className="text-gray-400 text-[8px] uppercase">P</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action mode buttons */}
      {(actionMode === 'gatepass' || actionMode === 'bill') && selectedRows.length > 0 && (
        <div className="fixed sm:sticky sm:top-[150px] bottom-0 z-50 bg-white p-3 border-t shadow-sm flex flex-wrap justify-center gap-2 sm:gap-4 no-print w-full">
          {(actionMode === 'bill' && showMoInputs) || actionMode === 'gatepass' ? (
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full sm:w-auto">
              {actionMode === 'bill' && showMoInputs && (
                <>
                  <div className="w-full sm:w-auto">
                    <label className="block text-sm font-medium text-gray-700">Work Order No</label>
                    <input
                      type="text"
                      value={workOrderNo}
                      onChange={(e) => setWorkOrderNo(e.target.value)}
                      className="border px-3 py-1 rounded text-sm w-full"
                      required
                    />
                  </div>
                  <div className="w-full sm:w-auto">
                    <label className="block text-sm font-medium text-gray-700">Work Order Date</label>
                    <input
                      type="date"
                      value={workOrderDate}
                      onChange={(e) => setWorkOrderDate(e.target.value)}
                      className="border px-3 py-1 rounded text-sm w-full"
                      required
                    />
                  </div>
                </>
              )}
            </div>
          ) : null}
          <div className="flex gap-2 sm:gap-4">
            <button
              onClick={actionMode === 'gatepass' ? handleGenerateGatePass : handleGenerateBill}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 rounded-lg flex items-center gap-2 text-sm sm:text-base"
            >
              <FaCheck />
              {actionMode === 'bill' && showMoInputs ? 'Generate Bill' : actionMode === 'gatepass' ? 'Generate Gate Pass' : 'Next'}
            </button>
            <button
              onClick={() => {
                if (actionMode === 'bill' && showMoInputs) {
                  setShowMoInputs(false);
                } else {
                  cancelAction();
                }
              }}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 sm:px-6 py-2 rounded-lg flex items-center gap-2 text-sm sm:text-base"
            >
              <FaTimes /> {actionMode === 'bill' && showMoInputs ? 'Back' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className={`print-area-wrapper px-2 sm:px-4 ${(actionMode === 'gatepass' || actionMode === 'bill') && selectedRows.length > 0 ? 'sm:mt-0 mb-[80px] sm:mb-0' : ''}`} ref={printRef}>
        {Object.entries(grouped).length === 0 ? (
          <div className="print-area bg-white p-6 text-center">
            <FaFileAlt className="mx-auto text-4xl text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700">No entries found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          Object.entries(grouped)
            .sort((a, b) => {
              const dateA = new Date(a[1][0]?.date || 0).getTime();
              const dateB = new Date(b[1][0]?.date || 0).getTime();
              if (dateB !== dateA) return dateB - dateA;
              return parseInt(b[0] || 0) - parseInt(a[0] || 0);
            })
            .map(([challanNo, rows], index) => (
            <div key={index} className="print-area bg-white p-4 sm:p-8 mb-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 print:mb-0 print:border-b print:border-gray-200">
              {/* Challan header */}
              <div className="challan-header pb-3 mb-3 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-base sm:text-lg bg-blue-50 text-blue-700 px-4 py-1.5 rounded-xl border border-blue-100 inline-flex items-center shadow-sm">
                      <span className="hidden sm:inline">Challan No:</span> {challanNo}
                    </h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${rows[0]?.completed ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                      {rows[0]?.completed ? 'Completed' : 'Pending'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 sm:gap-2 text-xs">
                    <span className="bg-gray-100 px-2 py-1 rounded-full">Date: {formatDate(rows[0]?.date) || '-'}</span>
                    <span className="bg-gray-100 px-2 py-1 rounded-full">Vehicle: {rows[0]?.vehicleNo || '-'}</span>
                  </div>
                </div>
                <div className="sm:hidden mt-2">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {/* Received */}
                    <div className="bg-blue-50 px-2 py-1 rounded flex items-center">
                      <span className="font-medium whitespace-nowrap">Received:</span>
                      {actionMode === 'edit' && editMode && selectedRows.some(id => rows.some(r => r.id === id)) ? (
                        <input type="number" step="0.01" className="ml-1 border border-blue-300 rounded px-1 w-16 text-blue-900 bg-white focus:outline-none" value={editedChallanTotals[challanNo]?.received !== undefined ? editedChallanTotals[challanNo].received : getChallanTotals(rows).received} onChange={(e) => setEditedChallanTotals(prev => ({ ...prev, [challanNo]: { ...(prev[challanNo] || {}), received: e.target.value } }))} />
                      ) : (
                        <span className="ml-1">{getChallanTotals(rows).received} L</span>
                      )}
                    </div>
                    {/* Total Oil in % */}
                    <div className="bg-blue-50 px-2 py-1 rounded flex items-center">
                      <span className="font-medium whitespace-nowrap">Total Oil:</span>
                      {actionMode === 'edit' && editMode && selectedRows.some(id => rows.some(r => r.id === id)) ? (
                        <input type="number" step="0.01" className="ml-1 border border-blue-300 rounded px-1 w-16 text-blue-900 bg-white focus:outline-none" value={editedChallanTotals[challanNo]?.totalOilWithPercentage !== undefined ? editedChallanTotals[challanNo].totalOilWithPercentage : getChallanTotals(rows).totalOilWithPercentage} onChange={(e) => setEditedChallanTotals(prev => ({ ...prev, [challanNo]: { ...(prev[challanNo] || {}), totalOilWithPercentage: e.target.value } }))} />
                      ) : (
                        <span className="ml-1">{getChallanTotals(rows).totalOilWithPercentage} L</span>
                      )}
                    </div>
                    {/* Oil Challan */}
                    <div className="bg-blue-50 px-2 py-1 rounded flex items-center">
                      <span className="font-medium whitespace-nowrap">Oil Chl:</span>
                      {actionMode === 'edit' && editMode && selectedRows.some(id => rows.some(r => r.id === id)) ? (
                        <input type="text" className="ml-1 border border-blue-300 rounded px-1 w-16 text-blue-900 bg-white focus:outline-none" value={editedChallanTotals[challanNo]?.oilChallanNo !== undefined ? editedChallanTotals[challanNo].oilChallanNo : getChallanTotals(rows).oilChallanNo} onChange={(e) => setEditedChallanTotals(prev => ({ ...prev, [challanNo]: { ...(prev[challanNo] || {}), oilChallanNo: e.target.value } }))} />
                      ) : (
                        <span className="ml-1">{getChallanTotals(rows).oilChallanNo}</span>
                      )}
                    </div>
                    {/* Used */}
                    <div className="bg-blue-50 px-2 py-1 rounded flex items-center">
                      <span className="font-medium whitespace-nowrap">Used:</span>
                      {actionMode === 'edit' && editMode && selectedRows.some(id => rows.some(r => r.id === id)) ? (
                        <input type="number" step="0.01" className="ml-1 border border-blue-300 rounded px-1 w-16 text-blue-900 bg-white focus:outline-none" value={editedChallanTotals[challanNo]?.used !== undefined ? editedChallanTotals[challanNo].used : getChallanTotals(rows).used} onChange={(e) => setEditedChallanTotals(prev => ({ ...prev, [challanNo]: { ...(prev[challanNo] || {}), used: e.target.value } }))} />
                      ) : (
                        <span className="ml-1">{getChallanTotals(rows).used} L</span>
                      )}
                    </div>
                    {/* Total KG */}
                    <div className="bg-blue-50 px-2 py-1 rounded flex items-center">
                      <span className="font-medium whitespace-nowrap">Total KG:</span> 
                      {actionMode === 'edit' && editMode && selectedRows.some(id => rows.some(r => r.id === id)) ? (
                        <input type="number" step="0.01" className="ml-1 border border-blue-300 rounded px-1 w-16 text-blue-900 bg-white focus:outline-none" value={editedChallanTotals[challanNo]?.totalKg !== undefined ? editedChallanTotals[challanNo].totalKg : getChallanTotals(rows).totalKg} onChange={(e) => setEditedChallanTotals(prev => ({ ...prev, [challanNo]: { ...(prev[challanNo] || {}), totalKg: e.target.value } }))} />
                      ) : (
                        <span className="ml-1">{getChallanTotals(rows).totalKg} kg</span>
                      )}
                    </div>
                    {/* Balance */}
                    <div className="col-span-2 bg-blue-50 px-2 py-1 rounded flex items-center">
                      <span className="font-medium whitespace-nowrap">Balance:</span>
                      {actionMode === 'edit' && editMode && selectedRows.some(id => rows.some(r => r.id === id)) ? (
                        <input type="number" step="0.01" className="ml-1 border border-blue-300 rounded px-1 w-16 text-blue-900 bg-white focus:outline-none" value={editedChallanTotals[challanNo]?.balance !== undefined ? editedChallanTotals[challanNo].balance : getChallanTotals(rows).balance} onChange={(e) => setEditedChallanTotals(prev => ({ ...prev, [challanNo]: { ...(prev[challanNo] || {}), balance: e.target.value } }))} />
                      ) : (
                        <span className={`ml-1 ${Number(getChallanTotals(rows).balance) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{getChallanTotals(rows).balance} L</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="hidden sm:flex flex-wrap gap-2 text-xs sm:text-sm mt-2">
                  <div className="flex items-center bg-blue-50 px-3 py-1 rounded-full">
                    <FaOilCan className="mr-1 text-blue-400" />
                    <span className="font-medium whitespace-nowrap">Received:</span>
                    {actionMode === 'edit' && editMode && selectedRows.some(id => rows.some(r => r.id === id)) ? (
                      <input type="number" step="0.01" className="ml-2 border border-blue-300 rounded px-2 py-0.5 w-20 text-blue-900 bg-white focus:outline-none" value={editedChallanTotals[challanNo]?.received !== undefined ? editedChallanTotals[challanNo].received : getChallanTotals(rows).received} onChange={(e) => setEditedChallanTotals(prev => ({ ...prev, [challanNo]: { ...(prev[challanNo] || {}), received: e.target.value } }))} />
                    ) : (
                      <span className="ml-1">{getChallanTotals(rows).received} L</span>
                    )}
                  </div>
                  <div className="flex items-center bg-blue-50 px-3 py-1 rounded-full">
                    <span className="font-medium whitespace-nowrap">Total Oil in %:</span>
                    {actionMode === 'edit' && editMode && selectedRows.some(id => rows.some(r => r.id === id)) ? (
                      <input type="number" step="0.01" className="ml-2 border border-blue-300 rounded px-2 py-0.5 w-20 text-blue-900 bg-white focus:outline-none" value={editedChallanTotals[challanNo]?.totalOilWithPercentage !== undefined ? editedChallanTotals[challanNo].totalOilWithPercentage : getChallanTotals(rows).totalOilWithPercentage} onChange={(e) => setEditedChallanTotals(prev => ({ ...prev, [challanNo]: { ...(prev[challanNo] || {}), totalOilWithPercentage: e.target.value } }))} />
                    ) : (
                      <span className="ml-1">{getChallanTotals(rows).totalOilWithPercentage} L</span>
                    )}
                  </div>
                  <div className="flex items-center bg-blue-50 px-3 py-1 rounded-full">
                    <span className="font-medium whitespace-nowrap">Oil Challan:</span>
                    {actionMode === 'edit' && editMode && selectedRows.some(id => rows.some(r => r.id === id)) ? (
                      <input type="text" className="ml-2 border border-blue-300 rounded px-2 py-0.5 w-24 text-blue-900 bg-white focus:outline-none" value={editedChallanTotals[challanNo]?.oilChallanNo !== undefined ? editedChallanTotals[challanNo].oilChallanNo : getChallanTotals(rows).oilChallanNo} onChange={(e) => setEditedChallanTotals(prev => ({ ...prev, [challanNo]: { ...(prev[challanNo] || {}), oilChallanNo: e.target.value } }))} />
                    ) : (
                      <span className="ml-1">{getChallanTotals(rows).oilChallanNo}</span>
                    )}
                  </div>
                  <div className="flex items-center bg-blue-50 px-3 py-1 rounded-full">
                    <span className="font-medium whitespace-nowrap">Used:</span>
                    {actionMode === 'edit' && editMode && selectedRows.some(id => rows.some(r => r.id === id)) ? (
                      <input type="number" step="0.01" className="ml-2 border border-blue-300 rounded px-2 py-0.5 w-20 text-blue-900 bg-white focus:outline-none" value={editedChallanTotals[challanNo]?.used !== undefined ? editedChallanTotals[challanNo].used : getChallanTotals(rows).used} onChange={(e) => setEditedChallanTotals(prev => ({ ...prev, [challanNo]: { ...(prev[challanNo] || {}), used: e.target.value } }))} />
                    ) : (
                      <span className="ml-1">{getChallanTotals(rows).used} L</span>
                    )}
                  </div>
                  <div className="flex items-center bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
                    <span className="font-medium text-amber-700 whitespace-nowrap">Total KG:</span>
                    {actionMode === 'edit' && editMode && selectedRows.some(id => rows.some(r => r.id === id)) ? (
                      <input type="number" step="0.01" className="ml-2 border border-amber-300 rounded px-2 py-0.5 w-24 text-amber-900 bg-white focus:outline-none focus:ring-1 focus:ring-amber-500" value={editedChallanTotals[challanNo]?.totalKg !== undefined ? editedChallanTotals[challanNo].totalKg : getChallanTotals(rows).totalKg} onChange={(e) => setEditedChallanTotals(prev => ({ ...prev, [challanNo]: { ...(prev[challanNo] || {}), totalKg: e.target.value } }))} placeholder="Total KG" />
                    ) : (
                      <span className="ml-1 font-bold text-amber-800">{getChallanTotals(rows).totalKg} kg</span>
                    )}
                  </div>
                  <div className="flex items-center bg-blue-50 px-3 py-1 rounded-full">
                    <span className="font-medium whitespace-nowrap">Balance:</span>
                    {actionMode === 'edit' && editMode && selectedRows.some(id => rows.some(r => r.id === id)) ? (
                      <input type="number" step="0.01" className="ml-2 border border-blue-300 rounded px-2 py-0.5 w-20 text-blue-900 bg-white focus:outline-none" value={editedChallanTotals[challanNo]?.balance !== undefined ? editedChallanTotals[challanNo].balance : getChallanTotals(rows).balance} onChange={(e) => setEditedChallanTotals(prev => ({ ...prev, [challanNo]: { ...(prev[challanNo] || {}), balance: e.target.value } }))} />
                    ) : (
                      <span className={`ml-1 ${Number(getChallanTotals(rows).balance) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{getChallanTotals(rows).balance} L</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-50 border-y border-gray-200">
                    <tr>
                      {actionMode && (
                        <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 no-print">Select</th>
                      )}
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">SR No</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">Job No</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">KVA</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">Make</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">Make Sr.No</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">Oil Cap (L)</th>
                      <th className="py-3 px-4 text-xs font-semibold text-amber-600 uppercase tracking-wider border-b border-gray-200">KG (Weight)</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">Final Challan</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">Dispatch Date</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">Work Order</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">M.O. No</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">Bill No</th>
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">Bill Date</th>
                      {actionMode === 'bill' && showMoInputs && (
                        <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 no-print">Enter M.O. No</th>
                      )}
                      <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        className={`${row.completed ? 'bg-white hover:bg-emerald-50' : 'bg-white hover:bg-rose-50'} border-b border-gray-100 transition-colors print:bg-white print:break-inside-avoid`}
                      >
                        {actionMode && (
                          <td className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 no-print">
                            {((actionMode === 'gatepass' && !row.completed) ||
                              (actionMode === 'edit') ||
                              (actionMode === 'delete') ||
                              (actionMode === 'bill' && row.completed && !row.workBillNo)) && (
                                <input
                                  type="checkbox"
                                  checked={selectedRows.includes(row.id)}
                                  onChange={() => toggleSelect(row.id)}
                                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                              )}
                          </td>
                        )}
                        <td className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">{row.srNo}</td>
                        <td className="py-3 px-4 text-sm font-semibold text-gray-900 min-w-[100px]">
                          {editMode && selectedRows.includes(row.id) ? (
                            <input
                              type="text"
                              className="border px-2 py-1 rounded w-full sm:w-24 focus:ring-blue-500 focus:border-blue-500 no-print"
                              value={editedData[row.id]?.jobNo || ''}
                              onChange={(e) => handleChange(row.id, 'jobNo', e.target.value)}
                            />
                          ) : (
                            row.jobNo
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700 min-w-[60px]">
                          {editMode && selectedRows.includes(row.id) ? (
                            <input
                              type="text"
                              className="border px-2 py-1 rounded w-full sm:w-24 focus:ring-blue-500 focus:border-blue-500 no-print"
                              value={editedData[row.id]?.kva || ''}
                              onChange={(e) => handleChange(row.id, 'kva', e.target.value)}
                            />
                          ) : (
                            row.kva
                          )}
                        </td>
                        <td className="p-2 border min-w-[100px]">
                          {editMode && selectedRows.includes(row.id) ? (
                            <input
                              type="text"
                              className="border px-2 py-1 rounded w-full sm:w-24 focus:ring-blue-500 focus:border-blue-500 no-print"
                              value={editedData[row.id]?.make || ''}
                              onChange={(e) => handleChange(row.id, 'make', e.target.value)}
                            />
                          ) : (
                            row.make
                          )}
                        </td>
                        <td className="p-2 border min-w-[100px]">
                          {editMode && selectedRows.includes(row.id) ? (
                            <input
                              type="text"
                              className="border px-2 py-1 rounded w-full sm:w-24 focus:ring-blue-500 focus:border-blue-500 no-print"
                              value={editedData[row.id]?.makeSrNo || ''}
                              onChange={(e) => handleChange(row.id, 'makeSrNo', e.target.value)}
                            />
                          ) : (
                            row.makeSrNo || '-'
                          )}
                        </td>
                        <td className="p-2 border min-w-[80px]">
                          {editMode && selectedRows.includes(row.id) ? (
                            <input
                              type="number"
                              step="0.01"
                              className="border px-2 py-1 rounded w-full sm:w-24 focus:ring-blue-500 focus:border-blue-500 no-print"
                              value={editedData[row.id]?.oilCap || ''}
                              onChange={(e) => handleChange(row.id, 'oilCap', e.target.value)}
                            />
                          ) : (
                            row.oilCap
                          )}
                        </td>
                        {/* KG (Weight) column */}
                        <td className="p-2 border min-w-[80px] bg-amber-50">
                          {editMode && selectedRows.includes(row.id) ? (
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="border px-2 py-1 rounded w-full sm:w-20 focus:ring-amber-500 focus:border-amber-500 no-print bg-amber-50"
                              value={editedData[row.id]?.kg || ''}
                              onChange={(e) => handleChange(row.id, 'kg', e.target.value)}
                              placeholder="0.00"
                            />
                          ) : (
                            <span className="font-medium text-amber-800">{row.kg || '-'}</span>
                          )}
                        </td>
                        <td className="p-2 border min-w-[100px]">
                          {editMode && selectedRows.includes(row.id) ? (
                            <input
                              className="border px-2 py-1 rounded w-full sm:w-24 focus:ring-blue-500 focus:border-blue-500 no-print"
                              value={editedData[row.id]?.finalChallanNo || ''}
                              onChange={(e) => handleChange(row.id, 'finalChallanNo', e.target.value)}
                            />
                          ) : (
                            row.finalChallanNo || '-'
                          )}
                        </td>
                        <td className="p-2 border min-w-[100px]">
                          {editMode && selectedRows.includes(row.id) ? (
                            <input
                              type="date"
                              className="border px-2 py-1 rounded w-full sm:w-28 focus:ring-blue-500 focus:border-blue-500 no-print"
                              value={editedData[row.id]?.dispatchDate || ''}
                              onChange={(e) => handleChange(row.id, 'dispatchDate', e.target.value)}
                            />
                          ) : (
                            formatDate(row.dispatchDate) || '-'
                          )}
                        </td>
                        <td className="p-2 border min-w-[100px]">{row.workOrderNo || '-'}</td>
                        <td className="p-2 border min-w-[100px]">{row.moNumber || '-'}</td>
                        <td className="p-2 border min-w-[100px]">{row.workBillNo || '-'}</td>
                        <td className="p-2 border min-w-[100px]">{formatDate(row.billDate) || '-'}</td>
                        {actionMode === 'bill' && showMoInputs && selectedRows.includes(row.id) && (
                          <td className="p-2 border no-print min-w-[120px]">
                            <input
                              type="text"
                              className="border px-2 py-1 rounded w-full focus:ring-blue-500 focus:border-blue-500"
                              value={moNumbers[row.id] || row.moNumber || ''}
                              onChange={(e) => setMoNumbers(prev => ({
                                ...prev,
                                [row.id]: e.target.value
                              }))}
                              placeholder="Enter M.O. No"
                              required
                            />
                          </td>
                        )}
                        <td className="p-2 border min-w-[80px]">
                          {editMode && selectedRows.includes(row.id) ? (
                            <select
                              className="border px-2 py-1 rounded w-full sm:w-24 focus:ring-blue-500 focus:border-blue-500 no-print"
                              value={editedData[row.id]?.completed ? 'completed' : 'pending'}
                              onChange={(e) => handleChange(row.id, 'completed', e.target.value === 'completed')}
                            >
                              <option value="pending">Pending</option>
                              <option value="completed">Completed</option>
                            </select>
                          ) : (
                            <span className={`status-badge inline-block px-2 py-1 rounded-full text-xs font-medium ${row.completed ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'} print:bg-transparent print:text-black`}>
                              {row.completed ? 'Completed' : 'Pending'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
      <style jsx>{`
        @media (max-width: 400px) {
          .challan-header .flex {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
        table {
          min-width: 1000px;
        }
        .overflow-x-auto::-webkit-scrollbar {
          height: 8px;
        }
        .overflow-x-auto::-webkit-scrollbar-thumb {
          background-color: #888;
          border-radius: 4px;
        }
        .overflow-x-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
      `}</style>
    </div>
  );
};

export default RegisterView;